import {
    ComponentPropsWithoutRef,
    ReactNode,
    createContext,
    useContext,
    useMemo,
    useState,
} from 'react';
import { UseFieldArrayReturn, useFieldArray } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { Button } from '@givebutter/primitives/button';
import { Asterisk, Label } from '@givebutter/primitives/label';
import { Separator } from '@givebutter/primitives/separator';
import { maskWholeNumber, unmaskNumber } from '@givebutter/util';
import { faPlus, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    CampaignItemBundleFormData,
    CampaignItemFormData,
    EventTicketTab,
    ItemFormType,
} from 'assets/js/pages/campaign-settings/campaign-event/types';
import * as Form from 'components/form';
import { useGivebutterFormContext } from 'components/form';
import { SelectListItem, UseFormSearchableSelect } from 'components/select';
import * as Tooltip from 'components/tooltip';
import { UploadFile } from 'components/upload';
import useDebounce from 'customHooks/useDebounce';
import { useInfiniteCampaignItems } from 'customHooks/useInfiniteCampaignItems';
import { CampaignItem } from 'types/domain';

interface ContactFormContainerProps extends ComponentPropsWithoutRef<'div'> {}

const CampaignItemFormContainer = (props: ContactFormContainerProps) => {
    const { className = '', ...rest } = props;

    return <div className={`flex flex-col gap-y-6 ${className}`} {...rest} />;
};

const emptyItemField = { value: '', quantity: '' };

type ItemFieldArrayType = UseFieldArrayReturn<
    CampaignItemFormSelectItemType,
    'bundles' | 'items',
    'id'
>;

interface BaseType {
    name: string;
    active: boolean;
    price: string;
    free: boolean;
    total_quantity: string;
    unlimited: boolean;
    description: string;
    pictures: UploadFile[];
}

interface CampaignItemFormContext {
    itemFieldArray: ItemFieldArrayType;
    ticket?: CampaignItem | null;
    campaignItemsIndexTab: EventTicketTab;
    hasTickets: boolean;
    totalItems: number;
}

const defaultContext: CampaignItemFormContext = {
    itemFieldArray: {} as ItemFieldArrayType,
    ticket: null,
    campaignItemsIndexTab: 'tickets',
    hasTickets: false,
    totalItems: 0,
};

const CampaignItemFormContext = createContext<CampaignItemFormContext>(defaultContext);

const useCampaignItemFormContext = () => {
    const context = useContext(CampaignItemFormContext);
    if (context === defaultContext) {
        throw new Error(
            'useCampaignItemFormContext must be used within a CampaignItemFormProvider',
        );
    }
    return context;
};

interface CampaignItemFormProviderProps {
    children: ReactNode;
    ticket?: CampaignItem | null;
    hasTickets?: boolean;
    totalItems?: number;
}

const CampaignItemFormProvider = (props: CampaignItemFormProviderProps) => {
    const { ticket, hasTickets = false, totalItems = 0, children } = props;
    const { control } = useGivebutterFormContext<CampaignItemFormSelectItemType>();
    const { pathname } = useLocation();
    const pathSegments = pathname.split('/');
    const campaignItemsIndexTab: EventTicketTab = pathSegments.includes('bundles')
        ? 'bundles'
        : 'tickets';
    const fieldArrayName = campaignItemsIndexTab === 'tickets' ? 'bundles' : 'items';

    const itemFieldArray = useFieldArray({
        name: fieldArrayName,
        control,
    });

    const value = useMemo(
        () => ({ itemFieldArray, ticket, campaignItemsIndexTab, hasTickets, totalItems }),
        [itemFieldArray, ticket, campaignItemsIndexTab, hasTickets, totalItems],
    );
    return (
        <CampaignItemFormContext.Provider value={value}>
            {children}
        </CampaignItemFormContext.Provider>
    );
};

type CampaignItemFormSelectItemType = CampaignItemBundleFormData | CampaignItemFormData;

const CampaignItemFormSelectItemHeader = () => {
    const { campaignItemsIndexTab, ticket, hasTickets } = useCampaignItemFormContext();

    const title = campaignItemsIndexTab === 'bundles' ? 'In this bundle' : 'Add to a bundle';
    const description =
        campaignItemsIndexTab === 'tickets'
            ? 'Add this ticket to an existing bundle by choosing a bundle and the quantity of tickets to allocate to it.'
            : 'Add tickets to this bundle by choosing a ticket and its quantity.';

    if (campaignItemsIndexTab === 'tickets' && !ticket?.is_bundle_item) {
        return null;
    }

    return (
        <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-2">
                <h2 className={'font-medium'}>{title}</h2>
                {hasTickets ? <p className="text-sm text-gray-600">{description}</p> : null}
            </div>
        </div>
    );
};

const CampaignItemFormSelectItem = () => {
    const { itemFieldArray, campaignItemsIndexTab, totalItems } = useCampaignItemFormContext();
    const { control, watch, path } = useGivebutterFormContext<CampaignItemFormSelectItemType>();
    const [searchItems, setSearchItems] = useState('');
    const debouncedSearchItems = useDebounce(searchItems);
    const isTicketTab = campaignItemsIndexTab === 'tickets';

    const { data, ref: itemsRef } = useInfiniteCampaignItems({
        query: debouncedSearchItems,
        isBundle: isTicketTab,
    });
    const { fields, remove, append } = itemFieldArray;
    const [items, bundles] = watch(['items', 'bundles']);

    const fieldArrayName = isTicketTab ? 'bundles' : 'items';
    const currentItems = isTicketTab ? bundles : items;
    const labelName = isTicketTab ? 'Bundle' : 'Ticket type';
    const addText = isTicketTab ? 'Add to more bundles' : 'Add more to bundle';
    const dataPages = data?.pages ?? [];

    const updatedItems = updateIdsWithAnswers(currentItems);
    const selectedItemQuantities = getSelectedItemQuantities(updatedItems);
    const availableSelectItems = getAvailableSelectedItems(dataPages, updatedItems);
    const hasNoSelectableItems =
        (availableSelectItems.length === 0 && !searchItems) || fields.length >= totalItems;

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
                <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-7">
                        <Label>
                            {labelName}
                            {!isTicketTab && <Asterisk />}
                        </Label>
                    </div>
                    <div className="col-span-5">
                        <Label>
                            QTY in bundle
                            {!isTicketTab && <Asterisk />}
                        </Label>
                    </div>
                </div>
                <div className="flex flex-col gap-4">
                    {fields.map((field, index) => (
                        <div key={field.id} className="flex flex-col">
                            <div className="grid grid-cols-12 gap-6">
                                <div className="col-span-7 flex flex-col gap-1">
                                    <UseFormSearchableSelect
                                        control={control}
                                        name={path(`${fieldArrayName}.${index}.value`)}
                                        localSearch={false}
                                        clearable={true}
                                        inputProps={{
                                            placeholder: `Select a ${
                                                isTicketTab ? 'bundle' : 'ticket'
                                            }`,
                                            onChange: e => {
                                                setSearchItems(e.currentTarget.value);
                                            },
                                        }}
                                        onSelectItem={value => {
                                            if (!value) {
                                                setSearchItems('');
                                            }
                                        }}
                                        defaultSelectedItems={field.value ? [field.value] : []}
                                        rules={{
                                            required: !isTicketTab && 'Must select a ticket.',
                                            validate: value =>
                                                validateItemSelection(value, updatedItems),
                                        }}
                                    >
                                        {availableSelectItems.length === 0 ? (
                                            <div className="cursor-not-allowed p-2 text-sm text-gray-500">
                                                No other {isTicketTab ? 'bundles' : 'tickets'}{' '}
                                                available
                                            </div>
                                        ) : (
                                            availableSelectItems.map((item: CampaignItem) => {
                                                return (
                                                    <SelectListItem
                                                        key={item.id}
                                                        value={{
                                                            id: item.id.toString(),
                                                            value: item,
                                                            label: item.name,
                                                        }}
                                                    >
                                                        {item.name}
                                                    </SelectListItem>
                                                );
                                            })
                                        )}
                                        <div ref={itemsRef} />
                                    </UseFormSearchableSelect>
                                    <Form.UseFormError
                                        name={path(`${fieldArrayName}.${index}.value`)}
                                    />
                                    {selectedItemQuantities[index] ? (
                                        <p className="text-xs">
                                            {!isTicketTab
                                                ? `${selectedItemQuantities[index]} tickets reserved for individual sale.`
                                                : `Available bundles: ${selectedItemQuantities[index]}`}
                                        </p>
                                    ) : null}
                                </div>
                                <div className="col-span-5">
                                    <div className="flex gap-2">
                                        <Form.Container>
                                            <Form.TextInputRoot
                                                name={path(`${fieldArrayName}.${index}.quantity`)}
                                                control={control}
                                                rules={{
                                                    required:
                                                        !isTicketTab && 'Must provide a quantity.',
                                                    min: {
                                                        value: 1,
                                                        message: 'The quantity must be at least 1.',
                                                    },
                                                }}
                                                inputRootProps={{
                                                    mask: 'whole-number',
                                                }}
                                            >
                                                <Form.TextInputForm
                                                    className="w-full"
                                                    placeholder={'5'}
                                                />
                                            </Form.TextInputRoot>
                                            <Form.UseFormError
                                                name={path(`${fieldArrayName}.${index}.quantity`)}
                                            />
                                        </Form.Container>
                                        {fields.length > 1 && (
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <Button
                                                        variant="transparent"
                                                        shape="square"
                                                        onClick={() => {
                                                            remove(index);
                                                        }}
                                                        aria-label="Remove"
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faTrash}
                                                            className="text-red-600"
                                                        />
                                                    </Button>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <div>Delete</div>
                                                        <Tooltip.Arrow inverse />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        )}
                                        {fields.length === 1 && !isTicketTab && (
                                            <Tooltip.Root>
                                                <Tooltip.Trigger asChild>
                                                    <span>
                                                        <Button
                                                            variant="transparent"
                                                            shape="square"
                                                            aria-label="Delete"
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={faTrash}
                                                                className="text-gray-300"
                                                            />
                                                        </Button>
                                                    </span>
                                                </Tooltip.Trigger>
                                                <Tooltip.Portal>
                                                    <Tooltip.Content inverse>
                                                        <div>
                                                            You need at least 1 item in this bundle.
                                                        </div>
                                                        <Tooltip.Arrow inverse />
                                                    </Tooltip.Content>
                                                </Tooltip.Portal>
                                            </Tooltip.Root>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <Button
                disabled={hasNoSelectableItems}
                onClick={() => {
                    setSearchItems('');
                    append(emptyItemField);
                }}
                size="sm"
                variant="outline"
                color="neutral"
            >
                <FontAwesomeIcon icon={faPlus} />
                {addText}
            </Button>
        </div>
    );
};

const toCampaignItemsRequest = (items: ItemFormType[]) => {
    return items
        .filter(t => getSelectedItemValue(t))
        .map(t => {
            const isNewItem = Array.isArray(t.value) && t.value.length;
            const formattedId = Number(
                isNewItem && typeof t.value[0] === 'object' && 'id' in t.value[0]
                    ? t.value[0].id
                    : t.id,
            );

            return {
                id: formattedId,
                bundle_id: formattedId,
                quantity_per_bundle: unmaskNumber(t.quantity),
            };
        });
};

const CampaignItemFormSelectSummary = () => {
    const { campaignItemsIndexTab } = useCampaignItemFormContext();
    const { watch } = useGivebutterFormContext<CampaignItemFormSelectItemType>();
    const [items, bundles, unlimited, totalQuantity] = watch([
        'items',
        'bundles',
        'unlimited',
        'total_quantity',
    ]);
    const isTicketsTab = campaignItemsIndexTab === 'tickets';
    const currentItems = isTicketsTab ? bundles : items;

    const updatedItems = updateIdsWithAnswers(currentItems);
    const totalItemsQuantity = getSelectItemTotalQuantity(updatedItems);
    const totalQuantityUnmasked = unmaskNumber(totalQuantity || 0);

    const isTotalUnlimited = unlimited || totalItemsQuantity === 'Unlimited';
    const bundleQuantity = isTotalUnlimited ? 0 : totalItemsQuantity;

    const total = isTotalUnlimited
        ? 'Unlimited'
        : maskWholeNumber(totalQuantityUnmasked + bundleQuantity);

    return (
        <div className="flex flex-col gap-2">
            <h2 className="font-medium">{isTicketsTab ? 'Ticket' : 'Bundle'} summary</h2>
            <div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-4">
                <div className="flex flex-col gap-1">
                    <div className="flex justify-between">
                        <p className="text-sm">Current individual ticket</p>
                        <p className="text-sm">
                            {unlimited ? 'Unlimited' : maskWholeNumber(totalQuantityUnmasked)}
                        </p>
                    </div>
                    <div className="flex justify-between">
                        <p className="text-sm">Tickets in bundle(s)</p>
                        <p className="text-sm">
                            {maskWholeNumber(totalItemsQuantity) || 'Unlimited'}
                        </p>
                    </div>
                </div>
                <Separator className={`bg-gray-200`} />
                <div className="flex justify-between">
                    <p className="text-sm font-medium">Total tickets available</p>
                    <p className="text-sm font-medium">{total}</p>
                </div>
            </div>
        </div>
    );
};

const CampaignItemFormSelectBundleSummary = () => {
    const { watch } = useGivebutterFormContext<CampaignItemFormSelectItemType>();
    const [items, unlimited, totalQuantity] = watch(['items', 'unlimited', 'total_quantity']);
    const bundleSummary = getBundleItemsSummary(unlimited, totalQuantity, items);

    const ticketSum = bundleSummary.reduce((sum, item) => {
        return typeof item?.quantity === 'number' ? sum + item.quantity : sum;
    }, 0);

    const totalTickets = unlimited ? 'Unlimited' : maskWholeNumber(ticketSum);

    return (
        <div className="flex flex-col gap-2">
            <h2>Bundle summary</h2>
            <div className="flex flex-col gap-2 rounded-lg bg-gray-50 p-4">
                <div className="flex flex-col gap-1">
                    {!bundleSummary.length && <p className="text-sm">No tickets have been added</p>}
                    {bundleSummary.map(summary => {
                        const individualDisplay = unlimited
                            ? 'Unlimited'
                            : maskWholeNumber(summary?.quantity);

                        const formattedLabel = Array.isArray(summary?.label)
                            ? summary?.label[0]?.label
                            : summary?.label;

                        return formattedLabel ? (
                            <div key={formattedLabel} className="flex justify-between">
                                <p className="text-sm">{formattedLabel}</p>
                                <p className="text-sm">{individualDisplay}</p>
                            </div>
                        ) : null;
                    })}
                </div>
                <Separator className={`bg-gray-200`} />
                <div className="flex justify-between">
                    <p className="text-sm font-medium">Total tickets added</p>
                    <p className="text-sm font-medium">{totalTickets}</p>
                </div>
            </div>
        </div>
    );
};

const getSelectedItemValue = (item: any) => {
    const selected = item.value;
    const hasValue = selected?.length;

    if (item?.id && hasValue) {
        return item;
    }

    if (!hasValue) {
        return '';
    }

    const isEditing = typeof selected === 'string';
    return isEditing ? selected : selected[0].value;
};

const validateItemSelection = (
    item: string | { label: string }[],
    currentItems: ItemFormType[],
) => {
    if (currentItems.length < 2) {
        return true;
    }

    if (!item) return 'No bundle found by that name.';
    const itemValue = typeof item === 'string' ? item : item[0]?.label;
    return itemValue ? true : 'No bundle found by that name.';
};

const getBundleItemsSummary = (
    unlimited: boolean,
    totalQuantity: string,
    items: ItemFormType[] = [],
) => {
    return items
        .map(item => {
            const result = getSelectedItemValue(item);
            const nameToUse = typeof result === 'string' ? result : result?.name || result?.value;

            if (!nameToUse) {
                return null;
            }

            const bundleQuantity = unmaskNumber(totalQuantity || 0);
            const itemQuantity = unmaskNumber(item.quantity ?? 0);
            const calculatedQuantity = bundleQuantity * itemQuantity;

            return {
                label: nameToUse || '',
                quantity: unlimited ? 'Unlimited' : calculatedQuantity,
            };
        })
        .filter(Boolean);
};

const getSelectItemTotalQuantity = (items: ItemFormType[]) => {
    let totalQuantity = 0;
    let isUnlimited = false;

    if (items.length && !items[0].value) {
        return totalQuantity;
    }

    items.forEach(item => {
        const selectedItem = getSelectedItemValue(item);

        if (selectedItem?.total_quantity === null) {
            isUnlimited = true;
            return;
        }

        const currentQuantity = item?.total_quantity ?? selectedItem.total_quantity;
        const itemQuantity = unmaskNumber(item?.quantity ?? 0);
        const selectedItemQuantity = unmaskNumber(currentQuantity || 0);

        if (itemQuantity) {
            totalQuantity += itemQuantity * selectedItemQuantity;
        }
    });

    return isUnlimited ? 'Unlimited' : totalQuantity;
};

const getSelectedItemQuantities = (items: ItemFormType[] = []) =>
    items.map(item => {
        const selectedItem = getSelectedItemValue(item);

        if (!selectedItem) {
            return '';
        }

        if (selectedItem.bundle_only) {
            return 'No';
        }

        const quantityToUse = item.remaining ?? selectedItem.remaining;
        return quantityToUse ?? 'Unlimited';
    });

const getAvailableSelectedItems = (dataPages: CampaignItem[], items: ItemFormType[] = []) => {
    const selectedItemIds = new Set(
        items
            .map(item => {
                const selectedItem = getSelectedItemValue(item);
                return (item?.id ?? selectedItem?.id)?.toString();
            })
            .filter(Boolean),
    );

    return dataPages.filter(dataPageItem => {
        const itemId = dataPageItem.id.toString();

        if (!selectedItemIds.has(itemId)) {
            return true;
        }

        const matchingItem = items.find(item => {
            const selectedItem = getSelectedItemValue(item);
            return (item?.id ?? selectedItem?.id)?.toString() === itemId;
        });

        return !matchingItem?.value || matchingItem.value.length === 0;
    });
};

interface AnswerItemValue {
    total_quantity: number;
    remaining: number;
    bundle_only: boolean;
}

const updateIdsWithAnswers = (items: ItemFormType[] = []) => {
    return items.map(item => {
        if (Array.isArray(item.value) && item.value.length > 0) {
            const answerItem = item.value[0];
            const answerItemValue = answerItem.value as AnswerItemValue;

            return {
                ...item,
                id: answerItem.id.toString(),
                total_quantity: answerItemValue.total_quantity,
                remaining: answerItemValue.remaining,
                bundle_only: answerItemValue.bundle_only,
            };
        }

        return {
            ...item,
            id: Array.isArray(item.value) && !item.value.length ? undefined : item.id,
        };
    });
};

const Root = CampaignItemFormProvider;
const Container = CampaignItemFormContainer;
const SelectItemHeader = CampaignItemFormSelectItemHeader;
const SelectItem = CampaignItemFormSelectItem;
const SelectSummary = CampaignItemFormSelectSummary;
const SelectBundleSummary = CampaignItemFormSelectBundleSummary;

export type { CampaignItemFormSelectItemType, ItemFormType, BaseType };

export {
    emptyItemField,
    toCampaignItemsRequest,
    //
    Root,
    Container,
    SelectItem,
    SelectItemHeader,
    SelectSummary,
    SelectBundleSummary,
    //
    CampaignItemFormProvider,
    CampaignItemFormContainer,
    CampaignItemFormSelectItem,
    CampaignItemFormSelectItemHeader,
    CampaignItemFormSelectSummary,
    CampaignItemFormSelectBundleSummary,
};
